/**
 * PR #279 — Auth simples com static tokens.
 *
 * Modelo: token opaco no localStorage. Fetch global é monkey-patched
 * pra injetar `Authorization: Bearer <token>` em qualquer request pra
 * `/api/v1/admin/*`. Zero mudança nas páginas admin existentes.
 *
 * Backend valida contra ADMIN_TOKENS env var. Sem Clerk, sem JWT, sem MFA.
 */
const STORAGE_KEY = "acordei_admin_token";

export function getAdminToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setAdminToken(token: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, token.trim());
  } catch {
    // localStorage bloqueado — operação noop
  }
}

export function clearAdminToken(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
}

/**
 * Decode opaco do role pelo "label" do token, se incluído.
 * Não confia nisso pra autorização — só pra UX (mostrar nome no header).
 */
export function isAdminLoggedIn(): boolean {
  return !!getAdminToken();
}

let patchInstalled = false;

/**
 * Monkey-patcha window.fetch UMA vez. Toda chamada pra `/api/v1/admin/*`
 * recebe automaticamente o header Authorization se um token estiver salvo.
 *
 * Chamado em main.tsx logo após mount do React.
 */
export function installAdminFetchPatch(): void {
  if (patchInstalled || typeof window === "undefined") return;
  patchInstalled = true;

  const origFetch = window.fetch.bind(window);

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    let url: string;
    if (typeof input === "string") {
      url = input;
    } else if (input instanceof URL) {
      url = input.toString();
    } else {
      url = input.url;
    }

    // Só intercepta rotas admin — E só same-origin.
    // PR #284 (bug-hunt): SEM o check de origin, um fetch pra
    // https://attacker.com/api/v1/admin/x vazaria o Bearer token admin
    // pro servidor do atacante. Agora só injeta se a URL for do MESMO
    // origin que a página (ou path relativo, que é sempre same-origin).
    if (url.includes("/api/v1/admin/")) {
      let sameOrigin = false;
      if (url.startsWith("/")) {
        // Path relativo — sempre same-origin
        sameOrigin = true;
      } else {
        try {
          sameOrigin =
            typeof window !== "undefined" &&
            new URL(url).origin === window.location.origin;
        } catch {
          sameOrigin = false;
        }
      }
      if (sameOrigin) {
        const token = getAdminToken();
        if (token) {
          const headers = new Headers(init?.headers || {});
          if (!headers.has("Authorization")) {
            headers.set("Authorization", `Bearer ${token}`);
          }
          init = { ...init, headers };
        }
      }
    }

    return origFetch(input as RequestInfo, init);
  };
}

/**
 * Gate explícito de dev/teste local pra acessar /admin/* sem login.
 * (Movido de lib/clerkAuth.ts — Clerk foi removido do projeto.)
 *
 * Triggers:
 *   1. localhost/127.0.0.1 (auto — DX zero-config em dev local)
 *   2. env VITE_ACORDEI_PERMITIR_ADMIN_SEM_AUTH=1 (build-time)
 *   3. ?dev-admin=1 na URL (persiste em sessionStorage)
 *
 * Em prod (domínio real, sem env), /admin/* exige login por senha.
 */
export function permitirAdminSemAuth(): boolean {
  if (typeof window !== "undefined" && window.location?.hostname) {
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1" || h === "::1" || h.endsWith(".localhost")) {
      return true;
    }
  }
  const envGate = import.meta.env?.VITE_ACORDEI_PERMITIR_ADMIN_SEM_AUTH;
  if (typeof envGate === "string" && envGate.trim() === "1") {
    return true;
  }
  if (typeof window !== "undefined" && window.location?.search) {
    const params = new URLSearchParams(window.location.search);
    if (params.get("dev-admin") === "1") {
      try {
        sessionStorage.setItem("acordei_dev_admin", "1");
      } catch {
        /* sessionStorage indisponível */
      }
      return true;
    }
  }
  if (typeof window !== "undefined") {
    try {
      return sessionStorage.getItem("acordei_dev_admin") === "1";
    } catch {
      return false;
    }
  }
  return false;
}
